import { Switch } from "@/components/ui/switch";
import { SERVICE_TYPES, VEHICLES, formatEuro, splitFare } from "@/lib/api";
import { FeeSplit } from "@/components/fleet/FeeSplit";
import { InvitePreview } from "@/components/fleet/InviteBlocks";
import { AddressInput } from "@/components/fleet/AddressInput";
import { MapPreview } from "@/components/fleet/MapPreview";
import { t } from "@/lib/i18n";

const pad = (n) => String(n).padStart(2, "0");
export const toLocalInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

const tomorrowAt = (h) => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(h, 0, 0, 0);
  return d;
};

export const emptyRide = () => ({ origin: "", destination: "", pickup_at: toLocalInput(tomorrowAt(9)), service_type: "Rientro a Vuoto", vehicle_category: VEHICLES[0].value, passengers: 1, luggage: 0, fare: "", invite_enabled: false, boost: false });

export const StepProgress = ({ step, labels }) => (
  <div className="grid grid-cols-3 gap-2" data-testid="ride-step-progress">
    {labels.map((l, i) => (
      <div key={l} className="flex flex-col gap-1.5">
        <span className={`h-1.5 rounded-full ${i <= step ? "bg-[#10B981]" : "bg-[#27272A]"}`} />
        <span className={`text-[11px] font-bold uppercase tracking-wide ${i === step ? "text-white" : "text-[#52525b]"}`}>{i + 1}. {l}</span>
      </div>
    ))}
  </div>
);

export const StepRoute = ({ form, set }) => (
  <div className="flex flex-col gap-2" data-testid="ride-step-route">
    <div className="block"><span className="ff-label">{t("📍 Da dove")}</span>
      <AddressInput autoFocus gps testid="ride-origin-input" inputClassName="h-16 text-xl font-bold" placeholder={t("Es. Firenze")} value={form.origin} onChange={set("origin")} onPick={set("map_point")} />
    </div>
    <div className="text-center text-[#10B981] text-2xl font-extrabold leading-none">➔</div>
    <div className="block"><span className="ff-label">{t("🏁 A dove")}</span>
      <AddressInput testid="ride-destination-input" inputClassName="h-16 text-xl font-bold" placeholder={t("Es. Milano Linate")} value={form.destination} onChange={set("destination")} onPick={set("map_point")} />
    </div>
    <MapPreview point={form.map_point} testid="ride-map" />
  </div>
);

const QUICK = [
  { id: "adesso", label: () => t("Adesso"), get: () => { const d = new Date(Date.now() + 15 * 60000); d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5); return d; } },
  { id: "tra-1-ora", label: () => t("Tra 1 ora"), get: () => { const d = new Date(Date.now() + 3600000); d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15 % 60); return d; } },
  { id: "domani-07-00", label: () => `${t("Domani")} 07:00`, get: () => tomorrowAt(7) },
  { id: "domani-09-00", label: () => `${t("Domani")} 09:00`, get: () => tomorrowAt(9) },
];

export const StepWhen = ({ form, set }) => (
  <div className="flex flex-col gap-3" data-testid="ride-step-when">
    <label className="block"><span className="ff-label">{t("Data e Ora del ritiro")}</span>
      <input required type="datetime-local" data-testid="ride-pickup-input" className="ff-field h-16 text-lg font-bold" value={form.pickup_at} onChange={set("pickup_at")} />
    </label>
    <div className="grid grid-cols-2 gap-2">
      {QUICK.map((q) => (
        <button key={q.id} type="button" data-testid={`ride-quick-time-${q.id}`} onClick={() => set("pickup_at")(toLocalInput(q.get()))} className="ff-chip !rounded-xl h-12">{q.label()}</button>
      ))}
    </div>
  </div>
);

const ChipGroup = ({ label, options, value, onChange, testid }) => (
  <div>
    <span className="ff-label">{label}</span>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o.value} type="button" data-testid={`${testid}-${o.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} data-active={value === o.value} onClick={() => onChange(o.value)} className="ff-chip">{t(o.label.split(" • ")[0])}</button>
      ))}
    </div>
  </div>
);

const Counter = ({ label, value, onChange, min, max, testid }) => (
  <div className="flex-1">
    <span className="ff-label">{label}</span>
    <div className="flex items-center justify-between rounded-xl border border-[#27272A] bg-black h-14 px-2">
      <button type="button" data-testid={`${testid}-minus`} onClick={() => onChange(Math.max(min, value - 1))} className="ff-chip !min-h-[40px] !px-4">−</button>
      <span data-testid={`${testid}-value`} className="ff-display text-2xl font-extrabold text-white">{value}</span>
      <button type="button" data-testid={`${testid}-plus`} onClick={() => onChange(Math.min(max, value + 1))} className="ff-chip !min-h-[40px] !px-4">+</button>
    </div>
  </div>
);

export const StepPrice = ({ form, set }) => {
  const { executor, platform } = splitFare(form.fare);
  return (
    <div className="flex flex-col gap-4" data-testid="ride-step-price">
      <label className="block"><span className="ff-label">{t("Prezzo della corsa (€)")}</span>
        <input autoFocus required type="number" min="1" step="0.01" inputMode="decimal" data-testid="ride-fare-input" className="ff-field h-20 text-4xl font-extrabold" placeholder="0,00" value={form.fare} onChange={set("fare")} />
      </label>
      <p data-testid="ride-fare-hint" className="text-sm font-semibold text-white -mt-2">
        {t("Incassi 90% all'istante")} <span className="text-[#10B981]">({formatEuro(executor)})</span> <span className="text-[#52525b]">|</span> {t("Quota gestione FLEETFLOW 10%")} <span className="text-[#94A3B8]">({formatEuro(platform)})</span>
      </p>
      <FeeSplit fare={form.fare} />
      <ChipGroup label={t("Tipo di servizio")} options={SERVICE_TYPES} value={form.service_type} onChange={set("service_type")} testid="ride-service-chip" />
      <ChipGroup label={t("Veicolo")} options={VEHICLES} value={form.vehicle_category} onChange={set("vehicle_category")} testid="ride-vehicle-chip" />
      <div className="flex gap-3">
        <Counter label={t("Passeggeri")} value={form.passengers} onChange={set("passengers")} min={1} max={19} testid="ride-passengers" />
        <Counter label={t("Valigie")} value={form.luggage} onChange={set("luggage")} min={0} max={30} testid="ride-luggage" />
      </div>
      {!form.invite_enabled && (
        <label data-testid="ride-boost-option" className="flex items-start gap-3 rounded-xl border border-amber-400/70 bg-amber-400/10 p-4 cursor-pointer">
          <input type="checkbox" data-testid="ride-boost-checkbox" checked={form.boost} onChange={(e) => set("boost")(e.target.checked)} className="mt-0.5 h-6 w-6 accent-[#10B981] shrink-0" />
          <span className="text-sm text-white font-semibold leading-snug">{t("⚡ Metti in cima alla bacheca e notifica i colleghi in zona (+ € 2,99)")}</span>
        </label>
      )}
      <div className="flex items-center justify-between gap-4 rounded-xl border border-[#27272A] bg-black p-4">
        <span className="text-sm text-white leading-snug">{t("Genera Link d'Invito con Protezione PIN per Collega di Fiduciaria")}</span>
        <Switch data-testid="toggle-pin-invite-link" checked={form.invite_enabled} onCheckedChange={(v) => { set("invite_enabled")(v); if (v) set("boost")(false); }} className="data-[state=checked]:bg-[#3B82F6] shrink-0 scale-125" />
      </div>
      {form.invite_enabled && <InvitePreview />}
    </div>
  );
};
